/**
 * Descarga del video ORIGINAL desde el reproductor (`<VideoPlayer>`).
 *
 * Las URLs de `/files` van firmadas en el path (`/files/<token>`); `?d=1` hace que el backend
 * responda `Content-Disposition: attachment` (ver `backend/src/routes/files.routes.js`). Se baja
 * con un ancla temporal y la sesión por cookie, NUNCA con `fetch` a un blob: los videos pesan
 * decenas de MB y no deben pasar por la memoria de la pestaña. Antes del clic se comprueba la URL
 * con un `HEAD` (solo cabeceras) para no navegar a un error 401/404 (round 2026-10-28; ver
 * `utils/descargaArchivo.ts`). El atributo `download` solo lo
 * respeta el navegador cuando el archivo es del mismo origen; entre `mialoja.cl` y
 * `api.mialoja.cl` manda el nombre que fija el backend en `Content-Disposition`: desde el round
 * 2026-10-28 es el nombre ORIGINAL del archivo (viaja firmado en el token, `attachUrls` lo pone
 * desde `originalName`; regla 32 de docs/seguridad.md), no el uuid de la key. Por eso aquí NO se
 * pasa por `fetch` + blob: el nombre llega solo y el video no se carga en memoria.
 */
import { formatBytes } from "./colaSubida";
import { descargarVerificado } from "./descargaArchivo";

/** Agrega `d=1` a una URL firmada, con `?` o `&` según ya traiga query, y antes de un `#`. */
export function urlDeDescargaVideo(url: string): string {
  const almohadilla = url.indexOf("#");
  const cuerpo = almohadilla >= 0 ? url.slice(0, almohadilla) : url;
  const hash = almohadilla >= 0 ? url.slice(almohadilla) : "";
  if (/[?&]d=1(&|$)/.test(cuerpo)) return url;
  return `${cuerpo}${cuerpo.includes("?") ? "&" : "?"}d=1${hash}`;
}

/** Peso legible («4,7 MB») o `null` si no se conoce. */
export function pesoLegible(bytes: number | null | undefined): string | null {
  const n = Number(bytes);
  return Number.isFinite(n) && n > 0 ? formatBytes(n) : null;
}

/**
 * Descarga el original: primero comprueba con un `HEAD` que el enlace responde (ver
 * `utils/descargaArchivo.ts`: sin esto, un token vencido reemplazaba la pestaña entera por el JSON
 * del error) y solo entonces dispara la descarga con un ancla temporal en la misma pestaña.
 * Devuelve `true` si la descarga arrancó; `false` si no, y en ese caso ya se avisó el motivo.
 */
export function descargarOriginal(url: string, nombre?: string): Promise<boolean> {
  return descargarVerificado(urlDeDescargaVideo(url), nombre);
}
